import React, { Component, Fragment } from 'react';
import axios from '../../axios-instance';
import classes from './Wallet.module.scss';
import Button from '../UI/Button/Button';
import ResponsiveDialog from '../UI/ResponsiveDialog/ResponsiveDialog';
import CatchHeader from '../UI/CatchHeader/CatchHeader';
import { DEV_MODE, devLogImpression } from '../../dev/devMode';

// status → [pill label, pill class]. 2=saved / 3=accepted read as "active"
// (primary), 4=redeemed as "done" (green), 5=clicked/viewed as muted history.
const STATUS = {
    2: ['Saved', classes.StatusSaved],
    3: ['Accepted', classes.StatusAccepted],
    4: ['Redeemed', classes.StatusRedeemed],
    5: ['Viewed', classes.StatusViewed],
};

class Wallet extends Component {

    state = {
        content: [],
        showModal: false,
        message: '',
        // Default landing view is the full activity log; 'saved' filters to the
        // items still kept in the wallet (status 2 saved / 3 accepted).
        activeTab: 'history'
    }

    componentDidMount() {
        this.getDeviceContent() 
    }

    getDeviceContent = async() => {
        try {
            const res = await axios.get(`/device/${this.props.device.uuid}/content?status=[2,3,4,5]&sort_by=updated_at&sort_order=desc`, {
                headers: {
                    'x-device-uuid': `${this.props.device.uuid}`
                }
            });
            if (res && res.data) {
                // Log the raw shape so we can confirm what the backend returns for
                // saved (status 2) / clicked (status 5) items and map the right
                // thumbnail/title/brand fields. (Owner asked to verify this live.)
                console.log('[wallet] /content?status response:', res.data);
                let content = Array.isArray(res.data) ? res.data.slice() : [];
                this.setState({content: content})
            }
        } catch(err) {
            console.error('Error: ', err)
        }
    }

    onClearWallet = async() => {
        try {
            const res = await axios.delete(`/device/${this.props.device.uuid}/content`, {
                headers: {
                    'x-device-uuid': `${this.props.device.uuid}`
                }     
            });
            if (res) {
                if (res.status === 200) {
                    this.setState({
                        showModal: true,
                        message: res.data.message
                    });
                }
            }
        } catch(err) {
            this.setState({
                showModal: true,
                message: 'Error clearing wallet'
            })
        }
    }

    closeModal = () => {
        this.setState({showModal: false})
    }

    statusLabel = (status) => (STATUS[Number(status)] || ['In Wallet'])[0];
    statusClass = (status) => (STATUS[Number(status)] || ['', ''])[1];

    // Map one backend content item to a card. Field access is defensive because
    // the saved/clicked list may carry a leaner shape than the full content
    // payload (confirm against the logged response in getDeviceContent).
    renderCard = (item) => {
        const thumb =
            item?.images?.thumbnail?.url ||
            item?.images?.hero?.url ||
            item?.thumbnail ||
            item?.hero ||
            '';
        const title = item?.display_name || item?.title || item?.brand || 'Saved item';
        const brand = item?.brand || '';
        return (
            <div className={classes.WalletCard} key={item?.id ?? title}>
                <div className={classes.WalletCardThumb}>
                    {thumb
                        ? <img src={thumb} alt={title} />
                        : <div className={classes.WalletCardThumbFallback} />}
                </div>
                <div className={classes.WalletCardBody}>
                    {brand && <div className={classes.WalletCardBrand}>{brand}</div>}
                    <div className={classes.WalletCardTitle}>{title}</div>
                    <span className={`${classes.WalletCardStatus} ${this.statusClass(item?.status)}`}>{this.statusLabel(item?.status)}</span>
                </div>
            </div>
        );
    }

    updateStatus = async (content) => {
        if (DEV_MODE) { devLogImpression(6); return; }
        try {
          const uri = `/device/${this.props.device.uuid}/content/${content.id}?status=${6}`;
          const res = await axios.put(
            uri,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'x-device-uuid': `${this.props.device.uuid}`
              }
            }
          );
          if (res && res.status === 200) {
            if (res.data && res.data.message) {
              console.log(res.data.message);
            }
          }
        } catch (err) {
            console.error('Error: ', err);
        }
    };
    
    render() {
        const TABS = [
            { key: 'history', label: 'History' },
            { key: 'saved', label: 'Saved' },
        ];
        const items = this.state.content || [];
        // "Saved" = items still kept in the wallet (saved = 2, accepted = 3).
        // "History" = the full activity log (everything we fetched, newest first).
        const savedItems = items.filter((i) => [2, 3].includes(Number(i?.status)));
        const counts = { history: items.length, saved: savedItems.length };
        const shown = this.state.activeTab === 'saved' ? savedItems : items;
        const hasAny = items.length > 0;

        return (
            <div className={classes.Container}>
                <ResponsiveDialog
                    walletModal={true}
                    open={this.state.showModal}
                    onClose={() => this.closeModal()}>
                       {this.state.message}
                </ResponsiveDialog>
                <div className={classes.WalletContainer}>
                    <CatchHeader />
                    <div className={classes.WalletInner}>
                        {hasAny ? (
                            <Fragment>
                                <div className={classes.WalletHeading}>Your Wallet</div>
                                <div className={classes.Segmented} role="tablist">
                                    {TABS.map((t) => (
                                        <button
                                            key={t.key}
                                            type="button"
                                            role="tab"
                                            aria-selected={this.state.activeTab === t.key}
                                            className={`${classes.SegmentBtn} ${this.state.activeTab === t.key ? classes.SegmentBtnActive : ''}`}
                                            onClick={() => this.setState({ activeTab: t.key })}>
                                            {t.label}
                                            <span className={classes.SegmentCount}>{counts[t.key]}</span>
                                        </button>
                                    ))}
                                </div>
                                {shown.length > 0 ? (
                                    <div className={classes.WalletList}>
                                        {shown.map(this.renderCard)}
                                    </div>
                                ) : (
                                    <div className={classes.TabEmpty}>
                                        {this.state.activeTab === 'saved'
                                            ? 'Nothing saved yet. Tap save on an offer to keep it here.'
                                            : 'No activity yet.'}
                                    </div>
                                )}
                                <div className={classes.ButtonContainer}>
                                    <Button
                                        onClick={this.onClearWallet}
                                        backgroundColor={'var(--a8-ink)'}
                                        btnText={'Clear Wallet'}></Button>
                                </div>
                            </Fragment>
                        ) : (
                            <div className={classes.WalletEmpty}>
                                <div className={classes.WalletTitle}>Your wallet is empty</div>
                                <div className={classes.WalletCopy}>
                                    Offers and rewards you open or save will show up here.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={classes.WalletFooterCopy}>
                        ©actv8me {new Date().getFullYear()} | Privacy Policy | info@actv8me.com
                    </div>
                </div>
            </div>
        );
    }

}

export default Wallet;
